import { FACEBOOK_CAPI_CONFIG, FACEBOOK_CAPI_CONFIG_2, FACEBOOK_CAPI_CONFIG_3, FACEBOOK_CAPI_CONFIG_4, FACEBOOK_EVENTS, EVENT_SOURCES, CONTENT_TYPES, DEFAULT_CURRENCY, DELIVERY_CATEGORIES } from './constants';
import { prepareUserData, formatOrderItems, getCurrentCurrency, getEventTimestamp, getEventSourceUrl, formatErrorResponse } from './utils';
import ShareChatAnalytics from '../ShareChatAnalytics';

class FacebookCAPIService {
    static #instance = null;

    constructor() {
        if (FacebookCAPIService.#instance) {
            return FacebookCAPIService.#instance;
        }
        FacebookCAPIService.#instance = this;
    }

    /**
     * Send event to Facebook CAPI
     * @param {string} eventName - Name of the event
     * @param {Object} userData - User data
     * @param {Object} customData - Custom event data
     * @returns {Promise} - API response
     */
    async sendEvent(eventName, userData, customData = {}) {
        try {
            const eventData = {
                data: [{
                    event_name: eventName,
                    event_time: getEventTimestamp(),
                    action_source: EVENT_SOURCES.WEBSITE,
                    event_source_url: getEventSourceUrl(),
                    user_data: prepareUserData(userData),
                    custom_data: customData
                }]
            };

            // Send to all four pixels
            const [response1, response2, response3, response4] = await Promise.all([
                fetch(`${FACEBOOK_CAPI_CONFIG.BASE_URL}?access_token=${FACEBOOK_CAPI_CONFIG.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(eventData)
                }),
                fetch(`${FACEBOOK_CAPI_CONFIG_2.BASE_URL}?access_token=${FACEBOOK_CAPI_CONFIG_2.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(eventData)
                }),
                fetch(`${FACEBOOK_CAPI_CONFIG_3.BASE_URL}?access_token=${FACEBOOK_CAPI_CONFIG_3.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(eventData)
                }),
                fetch(`${FACEBOOK_CAPI_CONFIG_4.BASE_URL}?access_token=${FACEBOOK_CAPI_CONFIG_4.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(eventData)
                })
            ]);

            const [result1, result2, result3, result4] = await Promise.all([
                response1.json(),
                response2.json(),
                response3.json(),
                response4.json()
            ]);

            console.log(`Facebook CAPI ${eventName} event responses:`, {
                pixel1: result1,
                pixel2: result2,
                pixel3: result3,
                pixel4: result4
            });

            return { result1, result2, result3, result4 };
        } catch (error) {
            console.error(`Facebook CAPI ${eventName} event error:`, error);
            return formatErrorResponse(error, eventName);
        }
    }

    /**
     * Track purchase event
     * @param {Object} orderData - Order data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackPurchase(orderData, userData) {
        try {
            // Track with ShareChat Analytics
            ShareChatAnalytics.trackPurchase(orderData);
            
            // Format order items properly for contents array (without name field)
            const items = formatOrderItems(orderData.new_ordered_items || orderData.items || []);
            
            // Get product names for content_name field (separate from contents array)
            const productNames = (orderData.new_ordered_items || orderData.items || []).map(
                item => item.subscription_plan?.plan_name || item.name || 'Unknown Product'
            );
            
            // Get UTM parameters if available
            const utmParams = localStorage.getItem('fleek_utm_params') 
                ? JSON.parse(localStorage.getItem('fleek_utm_params')) 
                : {};
                
            // Prepare event data with correct contents structure
            const eventData = {
                event_name: FACEBOOK_EVENTS.PURCHASE,
                event_time: getEventTimestamp(),
                action_source: EVENT_SOURCES.WEBSITE,
                event_source_url: getEventSourceUrl(),
                event_id: `purchase_${orderData.order_id || orderData.id}_${Date.now()}`,
                
                user_data: prepareUserData({
                    ...userData,
                    clientIp: userData.clientIp || '',
                }),
                
                custom_data: {
                    content_type: CONTENT_TYPES.PRODUCT,
                    content_ids: items.map(item => item.id),
                    content_name: productNames.join(', '), // This is separate from contents array
                    contents: items, // This now only has id, quantity, item_price
                    currency: orderData.details?.currency_type || orderData.currency_type || DEFAULT_CURRENCY,
                    num_items: items.length,
                    value: orderData.payment?.amount || orderData.total_amount || orderData.amount || 0,
                    order_id: orderData.order_id || orderData.id,
                    predicted_ltv: orderData.payment?.amount || orderData.total_amount || orderData.amount || 0,
                    status: orderData.status || 'completed',
                    payment_method: orderData.payment?.payment_method || orderData.payment_method,
                    discount_amount: orderData.amount_saved_dict?.amount_saved || orderData.discount || 0,
                    ...utmParams
                }
            };

            // Send client-side pixel events
            if (window.fbq) {
                window.fbq('track', 'Purchase', {
                    content_type: CONTENT_TYPES.PRODUCT,
                    content_ids: items.map(item => item.id),
                    content_name: productNames.join(', '),
                    contents: items,
                    currency: orderData.details?.currency_type || orderData.currency_type || DEFAULT_CURRENCY,
                    value: orderData.payment?.amount || orderData.total_amount || orderData.amount || 0
                });
            }
            
            // Send server-side events to all four pixels
            const [response1, response2, response3, response4] = await Promise.all([
                fetch(`${FACEBOOK_CAPI_CONFIG.BASE_URL}?access_token=${FACEBOOK_CAPI_CONFIG.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({data: [eventData]})
                }),
                fetch(`${FACEBOOK_CAPI_CONFIG_2.BASE_URL}?access_token=${FACEBOOK_CAPI_CONFIG_2.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({data: [eventData]})
                }),
                fetch(`${FACEBOOK_CAPI_CONFIG_3.BASE_URL}?access_token=${FACEBOOK_CAPI_CONFIG_3.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({data: [eventData]})
                }),
                fetch(`${FACEBOOK_CAPI_CONFIG_4.BASE_URL}?access_token=${FACEBOOK_CAPI_CONFIG_4.ACCESS_TOKEN}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({data: [eventData]})
                })
            ]);

            const [result1, result2, result3, result4] = await Promise.all([
                response1.json(),
                response2.json(),
                response3.json(),
                response4.json()
            ]);

            console.log('Facebook CAPI Purchase event responses:', {
                pixel1: result1,
                pixel2: result2,
                pixel3: result3,
                pixel4: result4
            });

            return { result1, result2, result3, result4 };
        } catch (error) {
            console.error(`Facebook CAPI Purchase event error:`, error);
            return formatErrorResponse(error, FACEBOOK_EVENTS.PURCHASE);
        }
    }

    /**
     * Track login event
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackLogin(userData) {
        return this.sendEvent(FACEBOOK_EVENTS.LOGIN, userData, {
            method: userData.method || 'phone_otp',
            success: true,
            currency_type: getCurrentCurrency()
        });
    }

    /**
     * Track add to cart event
     * @param {Object} product - Product data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackAddToCart(product, userData) {
        // Track with ShareChat Analytics
        ShareChatAnalytics.trackAddToCart(product);
        
        // Original Facebook tracking
        return this.sendEvent(FACEBOOK_EVENTS.ADD_TO_CART, userData, {
            content_type: CONTENT_TYPES.PRODUCT,
            content_ids: [product.subscription_plan_id],
            content_name: product.plan_name,
            currency: product.currency_type || DEFAULT_CURRENCY,
            value: product.discounted_cost || 0
        });
    }

    /**
     * Track initiate checkout event
     * @param {Object} cartData - Cart data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackInitiateCheckout(cartData, userData) {
        return this.sendEvent(FACEBOOK_EVENTS.INITIATE_CHECKOUT, userData, {
            currency: cartData.currency_type || DEFAULT_CURRENCY,
            value: cartData.total_amount || 0,
            num_items: cartData.items?.length || 0,
            content_ids: cartData.items?.map(item => item.subscription_plan?.subscription_plan_id)
        });
    }

    /**
     * Track payment event
     * @param {Object} paymentData - Payment data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackPayment(paymentData, userData) {
        return this.sendEvent(FACEBOOK_EVENTS.PAYMENT, userData, {
            status: paymentData.status,
            payment_id: paymentData.payment_id,
            order_id: paymentData.order_id,
            value: paymentData.amount,
            currency: paymentData.currency || DEFAULT_CURRENCY
        });
    }

    /**
     * Track view content event
     * @param {Object} contentData - Content data
     * @param {Object} userData - User data
     * @returns {Promise} - API response
     */
    async trackViewContent(contentData, userData) {
        return this.sendEvent(FACEBOOK_EVENTS.VIEW_CONTENT, userData, {
            content_type: contentData.content_type || CONTENT_TYPES.PRODUCT,
            content_ids: contentData.content_ids,
            content_name: contentData.content_name,
            content_category: contentData.content_category,
            currency: contentData.currency || DEFAULT_CURRENCY,
            value: contentData.value || 0
        });
    }
}

// Export singleton instance
export const facebookCAPI = new FacebookCAPIService(); 